import { useQuery } from '@tanstack/react-query'

import { type SelectedTimezone } from '@sparkai/utils'

import { apiClient } from '../../apiClient'

type PredictionMistakesParams = {
  programId?: string
  startDate?: string
  endDate?: string
  systemId?: string
  softwareVersion?: string
  predictionClasses?: string
  selectedTimezone?: SelectedTimezone
  enabled?: boolean
}

export type PredictionMistakeType = 'object_detection' | 'classification' | 'response'

export type PredictionMistake = {
  class: string
  mistakenBy: string
  type: PredictionMistakeType
  count: number
  share: number
  shareTrend: number | null
  countTrend: number | null
  autonomousSystemShare: number
}

export type PredictionMistakesResponse = {
  mistakes: PredictionMistake[]
  totalErrors: number
}

export const PREDICTION_MISTAKES_KEY = 'program-prediction-mistakes'

export function useGetPredictionMistakes(params: PredictionMistakesParams) {
  const { programId, startDate, endDate, selectedTimezone, enabled = true, ...queryParams } = params

  const cleanedParams = Object.fromEntries(
    Object.entries({ startDate, endDate, ...queryParams }).filter(
      ([, v]) => v !== undefined && v !== null && v !== ''
    )
  )

  const TEN_MINUTES = 1000 * 60 * 10

  return useQuery<PredictionMistakesResponse>({
    queryKey: [PREDICTION_MISTAKES_KEY, { programId, startDate, endDate, ...queryParams, selectedTimezone }],
    queryFn: async () => {
      const response = await apiClient.get(`/int/v3/programs/${programId}/metrics/prediction-mistakes`, {
        params: cleanedParams,
      })
      return response.data
    },
    enabled: enabled && Boolean(programId) && Boolean(startDate) && Boolean(endDate),
    refetchOnWindowFocus: false,
    staleTime: TEN_MINUTES,
  })
}
